import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtEUR, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { ArrowLeft, FileText, Plus, Mail, Phone, MapPin, Building, IdCard, User, Receipt, Calendar, LayoutDashboard } from "lucide-react";
import QuoteModal from "../components/QuoteModal";
import RiepiloghiTab from "./RiepiloghiTab";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

const TABS = [
  { k: "panoramica", label: "Panoramica", icon: LayoutDashboard },
  { k: "prenotazioni", label: "Prenotazioni", icon: Calendar },
  { k: "fatturazione", label: "Fatturazione", icon: FileText },
  { k: "riepiloghi", label: "Riepiloghi", icon: Receipt },
];

export default function ClienteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("panoramica");
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showQuote, setShowQuote] = useState(false);
  const [quoteToOpen, setQuoteToOpen] = useState(null);

  const load = async () => {
    const [c, b, q, d, v] = await Promise.all([
      api.get(`/customers/${id}`),
      api.get("/bookings", { params: { customer_id: id } }),
      api.get("/quotes", { params: { customer_id: id } }),
      api.get("/drivers"),
      api.get("/vehicles"),
    ]);
    setCustomer(c.data); setBookings(b.data); setQuotes(q.data);
    setDrivers(d.data); setVehicles(v.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const driversMap = useMemo(() => Object.fromEntries(drivers.map(d => [d.id, `${d.first_name || ""} ${d.last_name || ""}`.trim()])), [drivers]);
  const vehiclesMap = useMemo(() => Object.fromEntries(vehicles.map(v => [v.id, `${v.brand || ""} ${v.model || ""} · ${v.plate || ""}`.trim()])), [vehicles]);

  const stats = useMemo(() => {
    const active = bookings.filter(b => b.status !== "Cancellata");
    const revenue = active.reduce((s, b) => s + Number(b.price || 0), 0);
    const future = active.filter(b => b.date >= new Date().toISOString().slice(0, 10)).length;
    return { total: active.length, revenue, future };
  }, [bookings]);

  const removeQuote = async (qid) => {
    if (!confirm("Eliminare il preventivo?")) return;
    try { await api.delete(`/quotes/${qid}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!customer) return <div className="text-slate-500">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="cliente-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => nav("/clienti")} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-900"><ArrowLeft size={18}/></button>
        <div className="flex-1">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{customer.name}</h1>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mt-1">{customer.type} · {stats.total} servizi · {stats.future} futuri</div>
        </div>
        <Button data-testid="new-quote-btn" onClick={() => { setQuoteToOpen(null); setShowQuote(true); }} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><FileText size={16}/> Crea Preventivo</Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-800/80 flex items-center gap-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon; const active = tab === t.k;
          return (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-mono-num font-bold border-b-2 transition ${active ? "border-amber-500 text-amber-400" : "border-transparent text-slate-500 hover:text-slate-200"}`}
            >
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "panoramica" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5 space-y-2 text-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mb-2">Dati anagrafici</div>
            <Info icon={<Building size={14}/>} label="Tipo" value={customer.type}/>
            <Info icon={<Mail size={14}/>} label="Email" value={customer.email}/>
            <Info icon={<Phone size={14}/>} label="Telefono" value={customer.phone}/>
            <Info icon={<MapPin size={14}/>} label="Indirizzo" value={customer.address}/>
            <Info icon={<IdCard size={14}/>} label="P.IVA" value={customer.vat}/>
            <Info icon={<IdCard size={14}/>} label="C.F." value={customer.fiscal_code}/>
            <Info icon={<User size={14}/>} label="Referente" value={customer.contact_person}/>
          </div>

          <div className="lg:col-span-2 grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Servizi totali</div>
              <div className="mt-2 font-mono-num text-3xl font-bold text-white">{stats.total}</div>
            </div>
            <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Fatturato</div>
              <div className="mt-2 font-mono-num text-3xl font-bold text-amber-400">{fmtEUR(stats.revenue)}</div>
            </div>
            <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Servizi futuri</div>
              <div className="mt-2 font-mono-num text-3xl font-bold text-emerald-400">{stats.future}</div>
            </div>
          </div>
        </div>
      )}

      {tab === "prenotazioni" && (
        <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800/80 text-xs uppercase tracking-widest text-slate-500 font-mono-num">Storico Prenotazioni</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                <tr><th className="text-left p-3">N°</th><th>Data / Ora</th><th>Servizio</th><th>Percorso</th><th>Stato</th><th className="text-right">Prezzo</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {bookings.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Nessuna prenotazione</td></tr>}
                {bookings.map(b => (
                  <tr key={b.id} className="hover:bg-slate-900/40">
                    <td className="p-3 font-mono-num text-amber-400 text-xs">{b.booking_number}</td>
                    <td className="text-slate-300"><div>{b.date}</div><div className="text-xs text-slate-500 font-mono-num">{b.time}</div></td>
                    <td className="text-slate-400">{b.service_type}</td>
                    <td className="text-slate-400 text-xs">{b.pickup} → {b.destination}</td>
                    <td><StatusBadge status={b.status}/></td>
                    <td className="text-right text-amber-400 font-mono-num">{fmtEUR(b.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "fatturazione" && (
        <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800/80 flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num">Storico Preventivi</div>
            <Button size="sm" onClick={() => { setQuoteToOpen(null); setShowQuote(true); }} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-8"><Plus size={14}/> Nuovo</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                <tr>
                  <th className="text-left p-3">N°</th><th className="text-left">Data</th><th className="text-left">Stato</th>
                  <th className="text-right">N° righe</th><th className="text-right">Imponibile</th>
                  <th className="text-right">IVA</th><th className="text-right">Totale</th><th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {quotes.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">Nessun preventivo</td></tr>}
                {quotes.map(q => (
                  <tr key={q.id} className="hover:bg-slate-900/40" data-testid={`quote-row-${q.id}`}>
                    <td className="p-3 font-mono-num text-amber-400 text-xs">{q.quote_number}</td>
                    <td className="text-slate-300">{q.issue_date}</td>
                    <td><StatusBadge status={q.status}/></td>
                    <td className="text-right text-slate-300 font-mono-num">{(q.items || []).length}</td>
                    <td className="text-right text-slate-300 font-mono-num">{fmtEUR(q.subtotal)}</td>
                    <td className="text-right text-slate-500 font-mono-num">{fmtEUR(q.vat_amount)}</td>
                    <td className="text-right text-amber-400 font-mono-num font-bold">{fmtEUR(q.total)}</td>
                    <td className="pr-3">
                      <div className="flex justify-end gap-1">
                        <a href={`${API_BASE}/quotes/${q.id}/pdf`} target="_blank" rel="noreferrer" title="PDF" className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-sky-400"><FileText size={14}/></a>
                        <button onClick={() => { setQuoteToOpen(q); setShowQuote(true); }} className="text-xs px-2 py-1 rounded hover:bg-slate-800 text-slate-300 hover:text-amber-400">Apri</button>
                        <button onClick={() => removeQuote(q.id)} className="text-xs px-2 py-1 rounded hover:bg-slate-800 text-slate-500 hover:text-rose-400">Elimina</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "riepiloghi" && (
        <RiepiloghiTab customer={customer} driversMap={driversMap} vehiclesMap={vehiclesMap}/>
      )}

      {showQuote && <QuoteModal customer={customer} quoteToOpen={quoteToOpen} onClose={(s) => { setShowQuote(false); if (s) load(); }}/>}
    </div>
  );
}

function Info({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-slate-300">
      <span className="text-slate-500">{icon}</span>
      <span className="text-xs text-slate-500 w-16">{label}:</span>
      <span>{value}</span>
    </div>
  );
}
